// src/components/tokenPage/valuation/McFeesHistoryChart.tsx

import React, { useMemo, useState } from "react";
import TimeRangePicker from "@/components/common/TimeRangePicker";
import { DataCard } from "@/components/ui";
import { TimeSeriesChart } from "@/components/charts/base";
import { useTokenFundamentals } from "@/contexts/token";
import { sliceByRange } from "@/lib/metrics";

type Range = '90d' | '180d' | '365d' | 'All';

/**
 * McFeesHistoryChart - Shows historical Market Cap / Fees ratio
 * 
 * Uses TokenFundamentalsContext for data (no inline fetching).
 */
const McFeesHistoryChart: React.FC = () => {
  const [range, setRange] = useState<Range>('365d');
  const { mcToFeesHistory, isLoading } = useTokenFundamentals();

  const { dates, values } = useMemo(() => {
    if (mcToFeesHistory.dates.length === 0) {
      return { dates: [], values: [] };
    }
    
    const targetLen = range === '90d' ? 90 : range === '180d' ? 180 : range === '365d' ? 365 : mcToFeesHistory.dates.length;
    return sliceByRange(mcToFeesHistory.dates, mcToFeesHistory.values, targetLen);
  }, [mcToFeesHistory, range]);

  // Hide if no data available
  if (!isLoading && (!values.length || values.every(v => !Number.isFinite(v)))) {
    return null;
  }

  return (
    <DataCard
      title="Market Cap / Fees"
      subtitle="Historical ratio"
      icon="trending-up"
      iconColor="text-emerald-400"
      isLoading={isLoading}
      loadingVariant="chart"
      actions={
        <TimeRangePicker options={['90d','180d','365d','All'] as const} value={range} onChange={setRange} theme="blue" />
      }
    >
      <TimeSeriesChart
        dates={dates}
        series={[{ name: 'Market Cap / Fees', values, color: '#34D399', areaStyle: true }]}
        height={220}
        yAxisFormat="ratio"
        showMeanBands
        showLegend={false}
      />
    </DataCard>
  );
};

export default McFeesHistoryChart;
